import React, { useMemo } from 'react';
import { Button, Select } from 'antd';

import SourceMark from '../notifications/SourceMark';
import { allCurrencyOptionGroups, currencySearchFilter } from '../../utils/money';
import { describeCollectionCurrency } from '../../utils/collectionCurrency';

/**
 * Collection settings › Currency (docs/plans/localization-preferences.md §2,
 * the owner's ruling L3): the currency a new currency element on this
 * collection's cards starts in. A collection that sets one is never steered
 * by whoever is editing it; one that sets none lets each author's own
 * currency speak.
 *
 * The time zone field's shape (CollectionTimezoneField): the SourceMark says
 * where the value came from (This collection / Inherited from {collection} /
 * Not set), and a currency set here offers **Reset to inherited**. Inheriting,
 * the picker holds no value and shows the inherited currency as its
 * placeholder, so picking that same one pins it here. Saved by Save changes
 * with the rest of the tab.
 */
export default function CollectionCurrencyField( { collection, onChange, readOnly = false } ) {
	const view = describeCollectionCurrency( collection );
	const options = useMemo( () => allCurrencyOptionGroups(), [] );

	if ( ! view ) return null;
	const resettable = ! readOnly && ( view.own || view.ignored );

	return (
		<div className='collection-currency'>
			<div className='collection-currency__label-row'>
				<label htmlFor='collection-currency'>Currency</label>
				<SourceMark id='collection-currency-source' word={view.mark.word} phrase={view.mark.phrase} own={view.mark.own} />
			</div>
			<Select
				id='collection-currency'
				size='large'
				showSearch
				filterOption={currencySearchFilter}
				className='collection-currency__select'
				value={view.ownCode || undefined}
				placeholder={view.placeholder}
				options={options}
				onChange={next => onChange( 'currency', next )}
				aria-describedby='collection-currency-source collection-currency-hint'
			/>
			<div id='collection-currency-hint' className='form-panel__hint'>
				New currency elements on this collection&rsquo;s cards start in this currency.
			</div>
			{view.ignored ? (
				<div className='form-panel__hint collection-currency__ignored'>
					&ldquo;{view.ignored}&rdquo; is not a currency keepr knows, so this collection uses the one it inherits.
				</div>
			) : null}
			{resettable ? (
				<div className='collection-currency__reset-row'>
					<Button
						type='link'
						className='collection-currency__reset'
						aria-describedby='collection-currency-fallback'
						onClick={() => onChange( 'currency', null )}
					>
						Reset to inherited
					</Button>
					<span id='collection-currency-fallback' className='collection-currency__fallback'>{view.fallbackText}</span>
				</div>
			) : null}
		</div>
	);
}
